import { useCallback } from 'react';
import { useDialog } from '~/hooks/useDialog';
import type { Instant } from '~/utils/temporal';
import { useAsyncOperationsAtom } from './atoms';
import { useEventWarnings } from './useEventWarnings';

export function useAsyncOperations() {
  const { handleOpenDialog } = useDialog('AsyncOperationsDialog');

  const [asyncOperations, setAsyncOperations] = useAsyncOperationsAtom();
  const {
    handleSetEventIdsWithWarnings,
    handleSetEventIdWithWarning,
    handleRemoveEventIdWithWarning,
    handleClearAllWarnings,
    handlePanelClosed,
  } = useEventWarnings();

  const handleSetCursor = useCallback(
    (cursor: Instant | null) => {
      setAsyncOperations((prev) => {
        // Skip the write to localStorage when cursor value is the same.
        const isSameCursor = prev.nextAfter === cursor;
        const isEqualCursor = !!prev.nextAfter && !!cursor && prev.nextAfter.equals(cursor);

        return isSameCursor || isEqualCursor ? prev : { ...prev, nextAfter: cursor };
      });
    },
    [setAsyncOperations],
  );

  const handlePanelOpened = useCallback(
    (props?: Parameters<typeof handleOpenDialog>[0]) => {
      setAsyncOperations((prev) => ({ ...prev, hasUnreadEvents: false, isPanelOpen: true }));
      handleOpenDialog(props);
    },
    [setAsyncOperations, handleOpenDialog],
  );

  const handleSetHasUnreadEvents = useCallback(
    (hasUnreadEvents: boolean) => {
      setAsyncOperations((prev) => (prev.hasUnreadEvents === hasUnreadEvents ? prev : { ...prev, hasUnreadEvents }));
    },
    [setAsyncOperations],
  );

  return {
    asyncOperations,
    handleSetCursor,
    handlePanelOpened,
    handleSetHasUnreadEvents,
    handleSetEventIdsWithWarnings,
    handleSetEventIdWithWarning,
    handleRemoveEventIdWithWarning,
    handleClearAllWarnings,
    handlePanelClosed,
  };
}
